import cn from 'classnames'
import {useEffect, useMemo, useRef} from 'react'
import type {Control, ControllerRenderProps} from 'react-hook-form'
import {Controller} from 'react-hook-form'
import {CustomSelect} from 'slices/IntegrationsContainer/CustomSelect'
import type {FormData} from 'src/Event/hooks/useHubspotForm'

type SelectOption = {
  value: string
  label: string
}

type Props = {
  placeholder?: string
  control: Control<FormData>
  fieldName: string
  requiredMessage: string
  className?: string
  placeholderColor?: string
  options: readonly SelectOption[]
  isRequired?: boolean
}

type SelectFieldProps = {
  field: ControllerRenderProps<FormData, string>
  options: readonly SelectOption[]
  customSelectOptions: Array<{category_name: string; value: string}>
  isRequired: boolean
  requiredMessage: string
  placeholderColor?: string
}

const SelectField = ({
  field,
  options,
  customSelectOptions,
  isRequired,
  requiredMessage,
  placeholderColor,
}: SelectFieldProps) => {
  const currentIndex = field.value ? options.findIndex((opt) => opt.value === field.value) + 1 : 0
  const isPlaceholder = currentIndex === 0
  const hiddenInputRef = useRef<HTMLInputElement>(null)

  // To show validation message when using custom select
  useEffect(() => {
    if (hiddenInputRef.current) {
      if (isRequired && (!field.value || field.value === '')) {
        hiddenInputRef.current.setCustomValidity(requiredMessage)
      } else {
        hiddenInputRef.current.setCustomValidity('')
      }
    }
  }, [field.value, isRequired, requiredMessage])

  const handleIndexChange = (index: number) => {
    if (index === 0) {
      field.onChange('')
    } else {
      field.onChange(options[index - 1].value)
    }
  }

  return (
    <div className="relative">
      <input
        ref={hiddenInputRef}
        type="text"
        value={field.value || ''}
        required={isRequired}
        onChange={() => {}}
        className="pointer-events-none absolute inset-0 h-10 w-full opacity-0"
        tabIndex={-1}
        aria-hidden="true"
      />
      <div
        className={cn(
          '[&>div]:w-full',
          '[&>div>div]:w-full',
          '[&>div>div]:max-w-none',
          '[&_button]:h-10',
          '[&_button]:text-14',
          '[&_button]:leading-5',
          {
            '[&_button_span:first-child]:text-[var(--placeholder-color)]': isPlaceholder,
            '[&_button_span:first-child]:text-moss-black': !isPlaceholder,
          },
          '[&_button_svg]:!h-3',
          '[&_button_svg]:!w-3',
        )}
        style={
          isPlaceholder ? ({'--placeholder-color': placeholderColor || '#8f8d8b'} as React.CSSProperties) : undefined
        }
      >
        <CustomSelect
          options={customSelectOptions}
          activeIndex={currentIndex}
          setActiveIndex={handleIndexChange}
          trackingData={{
            dataType: 'event-registration-select',
            pageName: 'event-page',
          }}
          optionClassName="!text-moss-black"
        />
      </div>
    </div>
  )
}

export const Select = ({
  placeholder = 'Select an option',
  control,
  fieldName,
  requiredMessage,
  className,
  placeholderColor,
  options,
  isRequired = true,
}: Props) => {
  const displayPlaceholder = isRequired ? `${placeholder} *` : placeholder

  const customSelectOptions = useMemo(() => {
    const placeholderOption = {
      category_name: displayPlaceholder,
      value: '',
    }

    const mappedOptions = options.map((opt) => ({
      category_name: opt.label,
      value: opt.value,
    }))

    return [placeholderOption, ...mappedOptions]
  }, [options, displayPlaceholder])

  return (
    <div className={className}>
      <Controller
        name={fieldName}
        control={control}
        render={({field}) => (
          <SelectField
            field={field}
            options={options}
            customSelectOptions={customSelectOptions}
            isRequired={isRequired}
            requiredMessage={requiredMessage}
            placeholderColor={placeholderColor}
          />
        )}
      />
    </div>
  )
}
